import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Share2, History, Clock, Shield, Calendar, AlertTriangle } from 'lucide-react';
import ShareModal from '@/components/ShareModal';
import CertificateFileUpload from '@/components/CertificateFileUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERT_TYPES = [
  'SSL/TLS', 'Code Signing', 'Client Certificate', 'Server Certificate',
  'Wildcard', 'Multi-Domain', 'Self-Signed', 'Root CA'
];

const PROVIDERS = [
  "Let's Encrypt", 'DigiCert', 'GlobalSign', 'Sectigo (Comodo)',
  'GoDaddy', 'AWS Certificate Manager', 'Google Trust Services',
  'Cloudflare', 'ZeroSSL', 'Other'
];

const FORMATS = [
  { value: 'PEM', label: '.PEM' }, { value: 'DER', label: '.DER' },
  { value: 'P7B', label: '.P7B' }, { value: 'P12', label: '.P12' },
  { value: 'PFX', label: '.PFX' }, { value: 'CRT', label: '.CRT' },
  { value: 'CER', label: '.CER' }, { value: 'KEY', label: '.KEY' },
];

const calculateExpiryDays = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const getExpiryStatus = (days) => {
  if (days === null) return null;
  if (days < 0) return { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle };
  if (days <= 7) return { label: `Expires in ${days} day${days !== 1 ? 's' : ''}`, color: 'text-orange-600', bg: 'bg-orange-100', icon: AlertTriangle };
  if (days <= 30) return { label: `Expires in ${days} days`, color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Calendar };
  return { label: `Expires in ${days} days`, color: 'text-green-600', bg: 'bg-green-100', icon: Calendar };
};

export default function CertificateDetail({ user }) {
  const { id } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  const fetchCertificate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/certificates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificate(response.data);
      setEditData(response.data);
    } catch (err) {
      toast.error('Failed to load certificate');
      navigate('/dashboard/certificates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/certificates/${id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (err) {
      // History may not be available
    }
  }, [id]);

  useEffect(() => {
    fetchCertificate();
    fetchHistory();
  }, [fetchCertificate, fetchHistory]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/certificates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificate deleted successfully');
      navigate('/dashboard/certificates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete certificate');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/certificates/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificate updated successfully');
      setIsEditing(false);
      fetchCertificate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading...</div>
      </div>
    );
  }

  if (!certificate) return null;

  const days = calculateExpiryDays(certificate.expiry_date);
  const expiryStatus = getExpiryStatus(days);
  const ExpiryIcon = expiryStatus?.icon;

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/dashboard/certificates')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6 font-medium"
        data-testid="btn-back-to-certificates"
      >
        <ArrowLeft size={20} />
        Back to Certificates
      </button>

      <div className="max-w-3xl">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2" data-testid="cert-detail-title">
              {certificate.title}
            </h1>
            <p className="text-text-secondary">{certificate.certificate_type} &middot; {certificate.certificate_provider}</p>
          </div>
          <div className="flex gap-3">
            {certificate.can_edit && !isEditing && (
              <button onClick={() => setIsEditing(true)} className="btn-outline px-4 py-2" data-testid="btn-edit-certificate">
                <Edit size={18} />
              </button>
            )}
            {user?.role !== 'viewer' && certificate.can_edit && (
              <button onClick={() => setShowShareModal(true)} className="btn-outline px-4 py-2" data-testid="btn-share-certificate">
                <Share2 size={18} />
              </button>
            )}
            <button onClick={() => setShowHistory(!showHistory)} className="btn-outline px-4 py-2" data-testid="btn-view-cert-history">
              <History size={18} />
            </button>
            {certificate.can_delete && (
              <button onClick={handleDelete} className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent/90 transition-colors" data-testid="btn-delete-certificate">
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 rounded-xl p-8 space-y-6">
          {isEditing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Title</label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-edit-cert-title"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Type</label>
                  <select
                    value={editData.certificate_type}
                    onChange={(e) => setEditData({ ...editData, certificate_type: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                    data-testid="select-edit-cert-type"
                  >
                    {CERT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Provider</label>
                  <select
                    value={editData.certificate_provider}
                    onChange={(e) => setEditData({ ...editData, certificate_provider: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                    data-testid="select-edit-cert-provider"
                  >
                    {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Format</label>
                  <select
                    value={editData.certificate_format}
                    onChange={(e) => setEditData({ ...editData, certificate_format: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                    data-testid="select-edit-cert-format"
                  >
                    {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Expiry Date</label>
                  <input
                    type="date"
                    value={editData.expiry_date}
                    onChange={(e) => setEditData({ ...editData, expiry_date: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                    data-testid="input-edit-cert-expiry"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Domain Names</label>
                <input
                  type="text"
                  value={editData.domain_names || ''}
                  onChange={(e) => setEditData({ ...editData, domain_names: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary"
                  data-testid="input-edit-cert-domains"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Certificate Details</label>
                <textarea
                  value={editData.certificate_details || ''}
                  onChange={(e) => setEditData({ ...editData, certificate_details: e.target.value })}
                  rows={3}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                  data-testid="input-edit-cert-details"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Notes</label>
                <textarea
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg p-3 focus:border-primary focus:outline-none text-text-primary resize-none"
                  data-testid="input-edit-cert-notes"
                />
              </div>
              <div className="flex gap-4">
                <button onClick={handleUpdate} className="btn-primary flex-1" data-testid="btn-save-cert-changes">
                  Save Changes
                </button>
                <button
                  onClick={() => { setIsEditing(false); setEditData(certificate); }}
                  className="btn-outline flex-1"
                  data-testid="btn-cancel-cert-edit"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Certificate Info Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-text-muted mb-1">Type</p>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <p className="font-medium text-text-primary">{certificate.certificate_type}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-text-muted mb-1">Provider</p>
                  <p className="font-medium text-text-primary">{certificate.certificate_provider}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-text-muted mb-1">Format</p>
                  <p className="font-medium text-text-primary">{certificate.certificate_format}</p>
                </div>

                {expiryStatus && (
                  <div className={`rounded-lg p-4 ${expiryStatus.bg}`}>
                    <p className="text-xs text-text-muted mb-1">Expiry Date</p>
                    <div className="flex items-center gap-2">
                      {ExpiryIcon && <ExpiryIcon className={`w-4 h-4 ${expiryStatus.color}`} />}
                      <p className="font-medium text-text-primary">
                        {new Date(certificate.expiry_date).toLocaleDateString()}
                      </p>
                    </div>
                    <p className={`text-xs ${expiryStatus.color} font-medium mt-1`}>
                      {expiryStatus.label}
                    </p>
                  </div>
                )}
              </div>

              {certificate.domain_names && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-text-muted mb-2">Protected Domains</p>
                  <div className="flex flex-wrap gap-2">
                    {certificate.domain_names.split(',').map((domain) => (
                      <span
                        key={domain.trim()}
                        className="px-3 py-1 bg-white rounded-full text-sm text-text-primary border border-blue-200"
                      >
                        {domain.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {certificate.certificate_details && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-text-muted mb-2">Certificate Details</p>
                  <p className="text-sm text-text-primary whitespace-pre-wrap">{certificate.certificate_details}</p>
                </div>
              )}

              {certificate.notes && (
                <div>
                  <label className="block text-sm font-medium text-text-muted mb-2">Notes</label>
                  <p className="text-text-primary whitespace-pre-wrap" data-testid="display-cert-notes">{certificate.notes}</p>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-text-muted">
                  Created: {new Date(certificate.created_at).toLocaleString()}
                </p>
                <p className="text-sm text-text-muted">
                  Last updated: {new Date(certificate.updated_at).toLocaleString()}
                </p>
              </div>

              {/* Attachments */}
              <div className="pt-6 border-t border-gray-100">
                <CertificateFileUpload
                  certificateId={id}
                  attachments={certificate.attachments}
                  onUpdate={fetchCertificate}
                  canEdit={certificate.can_edit}
                />
              </div>
            </>
          )}
        </div>

        {/* History */}
        {showHistory && history.length > 0 && (
          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={20} className="text-primary" />
              <h3 className="font-manrope font-semibold text-lg text-text-primary">History</h3>
            </div>
            <div className="space-y-3">
              {history.map((log) => (
                <div key={log.id || `${log.timestamp}-${log.action}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                    <p className="text-xs text-text-secondary">
                      {log.user_name} ({log.user_email}) &bull; {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showShareModal && (
        <ShareModal
          resourceId={id}
          resourceType="certificate"
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            fetchCertificate();
            fetchHistory();
          }}
        />
      )}
    </div>
  );
}
